import api from '@/api/axios';

export interface RowStatus {
  id: number;
  nombre: string;
  descripcion: string;
}

export interface QuestionnaireType {
  id: number;
  nombre: string;
  rowStatus: RowStatus;
}

export interface QuestionnaireCategory {
  id: number;
  codigo: string;
  nombre: string;
  rowStatus: RowStatus;
}

export interface QuestionnaireGroupInfo {
  id: string;
  rowStatus?: RowStatus;
}

export interface QuestionnaireItem {
  id: number;
  nombre: string;
  fechaFin: string | null;
  categoriaCuestionario: QuestionnaireCategory;
  tipoCuestionario: QuestionnaireType;
  rowStatus: RowStatus;
  grupos?: QuestionnaireGroupInfo[];
  usuarios?: string[];
}

export interface QuestionnairePageable {
  pageSize: number;
  pageNumber: number;
  offset: number;
  paged: boolean;
  unpaged: boolean;
}

export interface QuestionnaireDataResponse {
  content: QuestionnaireItem[];
  pageable: QuestionnairePageable;
  totalPages: number;
  totalElements: number;
  last: boolean;
  first: boolean;
  numberOfElements: number;
  size: number;
  number: number;
  empty: boolean;
}

export interface QuestionnaireApiResponse {
  code: string;
  message: string;
  data: QuestionnaireDataResponse;
}

export interface QuestionnaireTypesResponse {
  code: string;
  message: string;
  data: {
    content: QuestionnaireType[];
    totalElements: number;
  };
}

export interface QuestionnaireCategoriesResponse {
  code: string;
  message: string;
  data: QuestionnaireCategory[];
}

export interface CreateQuestionnaireRequest {
  nombre: string;
  fechaLimite?: string;
  categoriaCuestionarioId: number;
  tipoCuestionarioId: number;
  grupos?: string[];
  usuarios?: string[];
}

export interface UpdateQuestionnaireRequest {
  cuestionarioId?: number;
  nombre: string;
  fechaLimite?: string;
  categoriaCuestionarioId: number;
  tipoCuestionarioId: number;
  grupos?: string[];
  usuarios?: string[];
}

export interface UserSearchResult {
  id: string;
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  idAco?: string;
}

export interface AnswerPayload {
  textoRespuesta: string;
  esCorrecta: boolean;
  puntos: number;
}

export interface QuestionPayload {
  textoPregunta: string;
  orden: number;
  puntos: number;
  tipoPreguntaId: number;
  respuestas?: AnswerPayload[];
}

export interface SaveQuestionsRequest {
  cuestionarioId: number;
  preguntas: QuestionPayload[];
}

export interface QuestionAnswer {
  id?: number;
  textoRespuesta: string;
  esCorrecta: boolean;
  puntos: number;
}

export interface QuestionItem {
  id?: number;
  textoPregunta: string;
  orden: number;
  puntos: number;
  tipoPreguntaId: number;
  tipoPregunta?: { id: number; nombre: string };
  respuestas?: QuestionAnswer[];
}

export interface QuestionTypeOption {
  id: number;
  nombre: string;
  descripcion: string;
  permiteOpciones: boolean;
  rowStatus: RowStatus;
}

export interface QuestionTypesResponse {
  code: string;
  message: string;
  data: QuestionTypeOption[];
}

export interface UserResponseItem {
  pregunta: string;
  respuesta: string;
}

export interface UserResponsesApiResponse {
  code: string;
  message: string;
  data: UserResponseItem[];
}

class QuestionnaireService {
  private readonly basePath = '/appy/cuestionario/v1';
  private readonly typesPath = '/appy/tipo/cuestionario/v1';
  private readonly categoriesPath = '/appy/categoria/cuestionario/v1';
  private readonly questionTypesPath = '/appy/tipo/pregunta/v1';

  async getItems(page: number = 1, size: number = 15, nombre?: string): Promise<QuestionnaireApiResponse> {
    const params = new URLSearchParams();
    params.append('page', String(page));
    params.append('size', String(size));
    if (nombre && nombre.trim()) {
      params.append('nombre', nombre.trim());
    }
    const response = await api.get<QuestionnaireApiResponse>(`${this.basePath}?${params.toString()}`);
    return response.data;
  }

  async getItemById(id: number): Promise<QuestionnaireApiResponse> {
    const response = await api.get<QuestionnaireApiResponse>(`${this.basePath}/${id}`);
    return response.data;
  }

  async getTypes(): Promise<QuestionnaireTypesResponse> {
    const response = await api.get<QuestionnaireTypesResponse>(this.typesPath);
    return response.data;
  }

  async getCategories(): Promise<QuestionnaireCategoriesResponse> {
    const response = await api.get<QuestionnaireCategoriesResponse>(this.categoriesPath);
    return response.data;
  }

  async createItem(data: CreateQuestionnaireRequest): Promise<QuestionnaireApiResponse> {
    const payload: Record<string, unknown> = {
      nombre: data.nombre,
      categoriaCuestionarioId: data.categoriaCuestionarioId,
      tipoCuestionarioId: data.tipoCuestionarioId
    };
    if (data.fechaLimite) payload.fechaLimite = data.fechaLimite;
    if (data.grupos && data.grupos.length > 0) payload.grupos = data.grupos;
    if (data.usuarios && data.usuarios.length > 0) payload.usuarios = data.usuarios;
    const response = await api.post<QuestionnaireApiResponse>(this.basePath, payload);
    return response.data;
  }

  async updateItem(id: number, data: UpdateQuestionnaireRequest): Promise<QuestionnaireApiResponse> {
    const payload: Record<string, unknown> = {
      cuestionarioId: id,
      nombre: data.nombre,
      categoriaCuestionarioId: data.categoriaCuestionarioId,
      tipoCuestionarioId: data.tipoCuestionarioId
    };
    if (data.fechaLimite) payload.fechaLimite = data.fechaLimite;
    if (data.grupos && data.grupos.length > 0) payload.grupos = data.grupos;
    if (data.usuarios && data.usuarios.length > 0) payload.usuarios = data.usuarios;
    const response = await api.put<QuestionnaireApiResponse>(this.basePath, payload);
    return response.data;
  }

  async deleteItem(id: number): Promise<void> {
    await api.delete(`${this.basePath}/${id}`);
  }

  async searchUsers(search: string): Promise<UserSearchResult[]> {
    const response = await api.get<{ data: { users: UserSearchResult[]; total: number } }>(
      `/admin/users?search=${encodeURIComponent(search)}&page=0&size=10`
    );
    return response.data.data.users;
  }

  async getQuestionTypes(): Promise<QuestionTypeOption[]> {
    const response = await api.get<QuestionTypesResponse>(this.questionTypesPath);
    return response.data.data.filter(t => t.rowStatus.id === 1);
  }

  async getQuestions(cuestionarioId: number): Promise<QuestionItem[]> {
    const response = await api.get<{ data: QuestionItem[] }>(
      `${this.basePath}/preguntas/${cuestionarioId}`
    );
    return response.data.data;
  }

  async saveQuestions(data: SaveQuestionsRequest): Promise<void> {
    await api.post(`${this.basePath}/preguntas`, data);
  }

  async deleteQuestion(questionId: number): Promise<void> {
    await api.delete(`${this.basePath}/preguntas/${questionId}`);
  }

  async getUserResponses(cuestionarioId: number, userUuid: string): Promise<UserResponsesApiResponse> {
    const response = await api.get<UserResponsesApiResponse>(
      `${this.basePath}/${cuestionarioId}/usuario/${userUuid}`
    );
    return response.data;
  }
}

export const questionnaireService = new QuestionnaireService();
export default questionnaireService;